import { useState, useEffect, useCallback } from 'react';
import {
  EditableCustomer,
  EditableCustomerUpdates,
  CustomerUpdateResult,
  getCustomerForEdit,
  updateCustomer,
} from '../services/customerUpdateApi';
import { invalidateCustomerIndexCache } from '../services/customerIndexCacheService';

export interface UseCustomerEditFormResult {
  customer: EditableCustomer | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  lastSaveResult: CustomerUpdateResult | null;
  refresh: () => void;
  save: (updates: EditableCustomerUpdates) => Promise<CustomerUpdateResult>;
}

export function useCustomerEditForm(
  dataverseCustomerId: string | null,
  enabled: boolean,
): UseCustomerEditFormResult {
  const [customer, setCustomer] = useState<EditableCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaveResult, setLastSaveResult] = useState<CustomerUpdateResult | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!dataverseCustomerId || !enabled) {
      setCustomer(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCustomerForEdit(dataverseCustomerId, signal);
      if (signal?.aborted) return;
      setCustomer(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load customer');
      setCustomer(null);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [dataverseCustomerId, enabled]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  const save = useCallback(async (updates: EditableCustomerUpdates): Promise<CustomerUpdateResult> => {
    if (!dataverseCustomerId) throw new Error('No customer selected');
    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await updateCustomer(dataverseCustomerId, updates);
      setLastSaveResult(result);
      if (result.customer) {
        setCustomer(result.customer);
      }
      invalidateCustomerIndexCache();
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save customer';
      setSaveError(message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [dataverseCustomerId]);

  return {
    customer,
    isLoading,
    isSaving,
    error,
    saveError,
    lastSaveResult,
    refresh,
    save,
  };
}
